@if (!(dataTable$ | async)?.length && !(isLoading$ | async) && !(isAnyFilterActive$ | async)) {
  <app-splash-screen title="Currently you’re not tracking progress for any items" class="flex-1">
    <div
      (click)="openTrackItemSidebar()"
      class="bg-blue-700 text-color-white rounded px-8 py-4 mt-5 mb-4 cursor-pointer"
    >
      Add Items to Track
    </div>
  </app-splash-screen>
}

<app-dynamic-projected-table
  [ngClass]="{ hidden: !(dataTable$ | async)?.length && !(isLoading$ | async) && !(isAnyFilterActive$ | async) }"
  [emptyTableMessage]="'There is no content that matches your filter option.'"
  [isLoading]="isLoading$ | async"
  [isEmpty]="!(dataTable$ | async)?.length"
  [wasDataLoadedOnce]="wasDataLoadedOnce"
  #dynamicProjectedTable
>
  <div tableHeader class="grid-table">
    <div class="grid-row header-row bg-navy-200">
      <app-checkbox-dropdown
        class="item"
        (selectedOptionsChange)="filterItemsByProgressType($event)"
      ></app-checkbox-dropdown>
      <div class="item"></div>
      <div class="item font-semibold text-sm text-navy-900">% Progress</div>
      <div class="item font-semibold text-sm text-navy-900">Due Date</div>
      <div class="flex items-center justify-end pr-2.5 col-span-3">
        <app-add-button (click)="openTrackItemSidebar()"> Add Item to Track </app-add-button>
      </div>
    </div>
  </div>

  <div projectedTable class="grid-table">
    @for (progressItem of dataTable$ | async; track trackById($index, progressItem)) {
      <app-expansion-panel
        [expanded]="expandedItems.get(progressItem.id)"
        (expandedChange)="expandedItems.set(progressItem.id, $event)"
      >
        <div header-row #tableRow class="grid-table">
          <div class="grid-row header-row">
            <div class="item title">
              <app-faded-text>
                {{ progressItem.title }}
              </app-faded-text>
              <app-styled-options-list
                class="flex items-center justify-center"
                (click)="$event.stopPropagation()"
                [listItems]="itemOptions"
                (selectedOption)="registerItemOption($event, progressItem)"
              >
                <span class="icon-options-inverted cursor-pointer"></span>
              </app-styled-options-list>
            </div>
            <app-activity-card-assign-button
              size="small"
              [activity]="itemActivity(progressItem)"
            ></app-activity-card-assign-button>
            <div class="item flex flex-col gap-1 progress">
              <app-progress-bar-projectable
                [percent]="progressItem.work_percentage"
                [allowBgColorChange]="false"
                percentWidth="big"
                fillClass="bg-navy-700"
                text="WORK"
              >
              </app-progress-bar-projectable>
              <app-progress-bar-projectable
                [percent]="progressItem.due_percentage"
                [isNegative]="progressItem.due_days < 0 && !progressItem?.checklist?.completed"
                [allowBgColorChange]="!!progressItem?.checklist?.completed"
                percentWidth="big"
                fillClass="bg-straw"
                text="DUE"
              >
                <app-days-count size="big" [dayCount]="progressItem.due_days" after-content></app-days-count>
              </app-progress-bar-projectable>
            </div>
            <div class="item text-xs font-normal text-navy-900">{{ progressItem.end_date }}</div>
            <div class="item pr-0">
              @if (progressItem?.is_due) {
                <span
                  [hideDelay]="100"
                  [appTooltip]="tooltipContent"
                  class="icon-alert-exclamation text-red-200 flex justify-center items-center"
                  tooltipPosition="right"
                ></span>
              }
              <ng-template #tooltipContent>
                @for (item of progressItem.checklist.items; track item.id) {
                  @if (item.due_days < 0) {
                    <div class="flex flex-column gap-5 w-max mb-2 last:mb-0">
                      <div class="flex items-center text-color-white font-semibold text-xs capitalize text-size_xs">
                        {{ item.name }}
                      </div>
                      <div class="flex items-center text-red-200 uppercase font-semibold text-size_xs">
                        {{ Math.abs(item.due_days) }} Days overdue
                      </div>
                    </div>
                  }
                }
              </ng-template>
            </div>
            <div class="separator"></div>
            <div class="item pl-5">
              {{ progressItem?.total_files }}
              @if (progressItem?.total_files === 1) {
                file
              } @else {
                files
              }
            </div>
          </div>
        </div>
        <div content-row class="grid-table">
          @for (item of progressItem?.checklist?.items; let checklistItemIndex = $index; track item.id) {
            <div class="parent-row">
              <div class="grid-row content-row pt-2">
                <div class="item subtitle">
                  <app-faded-text>
                    <div class="hover:bg-navy-100">
                      {{ item.name }}
                    </div>
                  </app-faded-text>
                  <div
                    class="flex items-center justify-center text-navy-400"
                    (click)="toggleItemCompletion(progressItem, checklistItemIndex)"
                    [ngClass]="{
                      '!text-green-600': !!item?.completed && !currentlyUpdating.has(item.id),
                      'icon-box-checked cursor-pointer': !currentlyUpdating.has(item.id),
                      'icon-refresh animate-spin': currentlyUpdating.has(item.id),
                      'opacity-50 cursor-not-allowed': disableUpdates,
                    }"
                  ></div>
                </div>
                <app-activity-card-assign-button
                  size="small"
                  [activity]="checklistItemActivity(progressItem, item)"
                ></app-activity-card-assign-button>
                <div class="item progress">
                  <app-progress-bar-projectable
                    [percent]="item.due_percentage"
                    [isNegative]="item.due_days < 0 && !item?.completed"
                    [allowBgColorChange]="item?.completed"
                    percentWidth="big"
                    fillClass="bg-straw"
                    text="DUE"
                  >
                    <app-days-count size="big" [dayCount]="item.due_days" after-content></app-days-count>
                  </app-progress-bar-projectable>
                </div>
                <div class="item text-xs font-normal text-navy-900">{{ item.end_date }}</div>
                <div
                  (click)="uploadWindowComponent.openWindow()"
                  class="item flex justify-center items-center cursor-pointer h-full box-border pr-0"
                >
                  <app-upload-window
                    class="flex items-center"
                    #uploadWindowComponent
                    (chosenFiles)="registerFileUpload($event, item.id)"
                    [allowMultipleFiles]="true"
                  >
                    <div class="icon-attachment-square text-blue-700"></div>
                  </app-upload-window>
                </div>
                <div class="separator second-row row-start-1 row-end-3 col-start-6"></div>
                <div class="row-start-1 row-end-3 col-start-7 item pl-5 pt-5 pb-5 h-full box-border">
                  @if (item?.files?.length) {
                    <div class="grid grid-cols-3 gap-x-2.5 gap-y-1.5 pr-4">
                      @for (file of item.files; track file) {
                        <div [showDelay]="300" [appTooltip]="file.name" tooltipPosition="bottom" class="file">
                          <div class="overflow-hidden text-ellipsis">
                            {{ file.name }}
                          </div>
                          <app-styled-options-list
                            (selectedOption)="registerFileSelected($event, file)"
                            [listItems]="fileOptions"
                            class="flex items-center justify-center"
                          >
                            <span class="icon-options-inverted text-base cursor-pointer"></span>
                          </app-styled-options-list>
                        </div>
                      }
                    </div>
                  } @else {
                    <div class="text-navy-600 font-semibold text-sm flex items-center">No files</div>
                  }
                </div>
              </div>
              <div class="grid-row content-row auto-rows pb-2">
                <div
                  [ngClass]="{ 'cursor-not-allowed': disableUpdates || currentlyUpdating.size > 0 }"
                  class="item note col-span-5 box-border pb-5 pr-5"
                >
                  <div
                    [ngClass]="{ disabled: disableUpdates || currentlyUpdating.size > 0 }"
                    (click)="textAreaElement.focus()"
                    class="textarea-wrapper cursor-text"
                  >
                    <textarea
                      class="flex"
                      #textAreaElement
                      rows="1"
                      cdkTextareaAutosize
                      [disabled]="disableUpdates || currentlyUpdating.size > 0"
                      [id]="'note_' + item.id"
                      [name]="'note_' + item.id"
                      [(ngModel)]="item.note"
                      (change)="updateItemNote(progressItem, checklistItemIndex, $event)"
                      placeholder="Optional Note (reporting will include top 5 lines)"
                    ></textarea>
                  </div>
                </div>
              </div>
            </div>
          }
        </div>
      </app-expansion-panel>
    }
  </div>

  <div tableFooter class="grid-table totals">
    <div class="grid-row">
      <div class="item">{{ (getTotals$ | async)?.totalItems }} Items</div>
      <div class="item"></div>
      <div class="item progress">
        <app-progress-bar-projectable
          [percent]="(getTotals$ | async)?.work_percentage"
          [allowBgColorChange]="false"
          percentWidth="big"
          fillClass="bg-navy-700"
          text="WORK"
        >
        </app-progress-bar-projectable>
      </div>
    </div>
  </div>
</app-dynamic-projected-table>
